import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getSessionSummary,
  updateIncidentNotes,
  createIncident,
  deleteIncident,
} from '../api/sessions'
import type { SessionSummary, IncidentResponse } from '../types'

export function useSessionSummary(month: string) {
  return useQuery({
    queryKey: ['session-summary', month],
    queryFn: () => getSessionSummary(month),
    enabled: !!month,
    staleTime: 0,
  })
}

export function useUpdateIncidentNotes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      incidentId,
      ...body
    }: {
      incidentId: string
      notes?: string
      investigationMinutes?: number
    }) => updateIncidentNotes(incidentId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incidents'] })
      qc.invalidateQueries({ queryKey: ['session-summary'] })
    },
  })
}

export function useCreateIncident() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createIncident,
    onSuccess: (created) => {
      // Optimistically inject the new incident into the cached summary
      qc.setQueriesData<SessionSummary>({ queryKey: ['session-summary'] }, (old) => {
        if (!old) return old
        const inc: IncidentResponse = {
          incidentId: created.incidentId,
          status: created.status,
          startedAt: created.startedAt,
          confirmedAt: created.confirmedAt,
          confirmedBy: created.confirmedBy,
          notes: created.notes,
          investigationMinutes: created.investigationMinutes,
        }
        const startedAt = new Date(created.startedAt).getTime()
        // Try to place into matching session, otherwise append as orphan
        let placed = false
        const sessions = old.sessions.map(s => {
          if (placed || !s.endTime) return s
          const sStart = new Date(s.startTime).getTime()
          const sEnd = new Date(s.endTime).getTime()
          if (startedAt >= sStart && startedAt < sEnd) {
            placed = true
            return { ...s, incidents: [...s.incidents, inc] }
          }
          return s
        })
        if (!placed) {
          sessions.push({ startTime: created.startedAt, incidents: [inc] })
          sessions.sort((a, b) => a.startTime.localeCompare(b.startTime))
        }
        return { ...old, sessions }
      })
      // Background refetch for authoritative data
      qc.invalidateQueries({ queryKey: ['incidents'] })
      qc.invalidateQueries({ queryKey: ['session-summary'] })
    },
  })
}

export function useDeleteIncident() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (incidentId: string) => deleteIncident(incidentId),
    onSuccess: (_data, incidentId) => {
      // Optimistically remove the incident from the cached summary
      qc.setQueriesData<SessionSummary>({ queryKey: ['session-summary'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          sessions: old.sessions
            .map(s => ({ ...s, incidents: s.incidents.filter(i => i.incidentId !== incidentId) }))
            // Remove orphan entries that now have zero incidents and no session title
            .filter(s => s.sessionId || s.incidents.length > 0),
        }
      })
      // Background refetch for authoritative data
      qc.invalidateQueries({ queryKey: ['incidents'] })
      qc.invalidateQueries({ queryKey: ['session-summary'] })
    },
  })
}

